/**
 * Reads Meta ad-click attribution identifiers from the browser at signup time.
 *
 * When a user clicks a Meta ad, Meta appends `fbclid` to the landing URL and the
 * Meta Pixel stores it in the `_fbc` cookie (`fb.1.<ts>.<fbclid>`) plus a browser
 * id in `_fbp`. Those cookies are set on the registrable domain
 * (`.emergecareer.com`), so they are readable across subdomains including the app.
 *
 * We persist these on the user doc so the server-side Conversions API `Lead`
 * event (fired at conditional acceptance, potentially days later) can send `fbc`/
 * `fbp` for deterministic ad-click attribution — no browser needed at that point.
 *
 * `fbc`/`fbp` are Meta's opaque cookie values; they are sent RAW (never hashed).
 * Only keys that resolve to a value are included.
 */
export interface MetaClickIds {
  fbc?: string;
  fbp?: string;
  fbclid?: string;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined" || !document.cookie) return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return undefined;
  const value = decodeURIComponent(match.slice(name.length + 1));
  return value || undefined;
}

export function getMetaClickIds(): MetaClickIds {
  const fbp = readCookie("_fbp");
  const cookieFbc = readCookie("_fbc");

  const fbclid =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("fbclid") || undefined
      : undefined;

  // Prefer the pixel-set `_fbc` cookie. If it's absent but we have the raw
  // `fbclid` from the URL, synthesize `fbc` in Meta's expected format.
  const fbc =
    cookieFbc ?? (fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined);

  const ids: MetaClickIds = {};
  if (fbc) ids.fbc = fbc;
  if (fbp) ids.fbp = fbp;
  if (fbclid) ids.fbclid = fbclid;
  return ids;
}
